/**
 * CTRL_07_ConfigPanel - Bias & Fairness Testing Configuration
 *
 * Level 1: Simple enable/disable toggle with status
 * Level 2: Advanced options (fairness metrics, protected attributes, bias threshold)
 */
import React, { useState, useEffect } from 'react';
import { Card, Switch, Button, Collapse, Select, Slider, Tag, Alert, Spin } from 'antd';
import {
  SettingOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  SaveOutlined,
  ExperimentOutlined
} from '@ant-design/icons';
import { getControlConfig, saveControlConfig } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';

const { Panel } = Collapse;
const { Option } = Select;

// Fairness metrics available
const FAIRNESS_METRICS = [
  { value: 'demographic_parity', label: 'Demographic Parity', description: 'Equal selection rates across groups' },
  { value: 'equalized_odds', label: 'Equalized Odds', description: 'Equal TPR/FPR across groups' },
  { value: 'disparate_impact', label: 'Disparate Impact (80% Rule)', description: 'Ratio >= 0.8' },
  { value: 'equal_opportunity', label: 'Equal Opportunity', description: 'Equal TPR across groups' }
];

// Protected attributes to test for bias
const PROTECTED_ATTRIBUTES = [
  { value: 'gender', label: 'Gender', icon: '⚧️' },
  { value: 'race', label: 'Race/Ethnicity', icon: '🌍' },
  { value: 'age', label: 'Age', icon: '🎂' },
  { value: 'ethnicity', label: 'Ethnicity', icon: '🌐' },
  { value: 'disability', label: 'Disability Status', icon: '♿' },
  { value: 'religion', label: 'Religion', icon: '⛪' }
];

function CTRL_07_ConfigPanel({ projectId, controlCode, project }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeKeys, setActiveKeys] = useState(['1']); // Default to level 1 open

  useEffect(() => {
    if (projectId && controlCode) {
      fetchConfig();
    }
  }, [projectId, controlCode]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await getControlConfig(projectId, controlCode);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
      // Set default config if fetch fails
      setConfig({
        enabled: false,
        config: {
          fairness_metrics: ['demographic_parity', 'disparate_impact'],
          protected_attributes: [],
          bias_threshold: 0.2,
          test_frequency: 'quarterly'
        }
      });
      if (error.response && error.response.status !== 404) {
        showError('Failed to load configuration. Using defaults.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveControlConfig(projectId, controlCode, config);
      showSuccess('Configuration saved successfully');
      setHasChanges(false);
      fetchConfig(); // Refresh to get server state
    } catch (error) {
      console.error('Failed to save config:', error);
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfig({
      ...config,
      config: {
        ...config.config,
        [field]: value
      }
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!config) return null;

  const configData = config.config || {};

  // Calculate status
  const isConfigured = config.enabled &&
                       configData.fairness_metrics &&
                       configData.fairness_metrics.length > 0 &&
                       configData.protected_attributes &&
                       configData.protected_attributes.length > 0;
  const statusIcon = isConfigured ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <WarningOutlined style={{ color: '#faad14' }} />;
  const statusText = isConfigured ? 'Configured' : 'Setup Required';

  return (
    <>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <SettingOutlined />
            <span>CTRL_07 Configuration: Bias & Fairness Testing</span>
            {statusIcon}
            <Tag color={isConfigured ? 'green' : 'orange'}>{statusText}</Tag>
          </div>
        }
        extra={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!hasChanges}
            >
              Save Changes
            </Button>
          </div>
        }
      >
        <Collapse
          activeKey={activeKeys}
          onChange={setActiveKeys}
          expandIconPosition="right"
        >
          {/* LEVEL 1: Simple Enable/Disable */}
          <Panel
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <strong>1. Enable Bias & Fairness Testing</strong>
                {config.enabled && <Tag color="green">Active</Tag>}
              </div>
            }
            key="1"
          >
            <div style={{ padding: '16px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                <Switch
                  checked={config.enabled}
                  onChange={(checked) => setConfig({ ...config, enabled: checked })}
                  size="large"
                />
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {config.enabled ? 'Bias Testing Enabled' : 'Bias Testing Disabled'}
                  </div>
                  <div style={{ color: '#888', fontSize: '13px' }}>
                    {config.enabled
                      ? 'TrustGrid is monitoring AI/ML models for fairness violations'
                      : 'Enable to start testing models for bias across protected attributes'}
                  </div>
                </div>
              </div>

              {!config.enabled && (
                <Alert
                  type="info"
                  message="About Bias & Fairness Testing"
                  description={
                    <div>
                      <p>This control helps ensure your AI models treat all demographic groups fairly by calculating fairness metrics such as:</p>
                      <ul style={{ marginTop: '8px', marginBottom: '8px' }}>
                        <li><strong>Demographic Parity:</strong> Equal selection rates across groups</li>
                        <li><strong>Disparate Impact:</strong> 80% rule compliance</li>
                        <li><strong>Equalized Odds:</strong> Equal TPR/FPR across groups</li>
                      </ul>
                      <p style={{ marginTop: '8px' }}>
                        Configure fairness metrics and protected attributes in Advanced Configuration.
                      </p>
                    </div>
                  }
                  showIcon
                  style={{ marginTop: '16px' }}
                />
              )}
            </div>
          </Panel>

          {/* LEVEL 2: Advanced Options */}
          <Panel
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <strong>2. Advanced Configuration</strong>
                {configData.fairness_metrics && configData.fairness_metrics.length > 0 && (
                  <Tag color="blue">{configData.fairness_metrics.length} metrics</Tag>
                )}
              </div>
            }
            key="2"
            disabled={!config.enabled}
          >
            <div style={{ padding: '16px 0' }}>
              {/* Fairness Metrics */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                  Fairness Metrics
                  <span style={{ fontWeight: 'normal', color: '#888', marginLeft: '8px' }}>
                    ({configData.fairness_metrics?.length || 0} selected)
                  </span>
                </label>
                <Select
                  mode="multiple"
                  value={configData.fairness_metrics || ['demographic_parity', 'disparate_impact']}
                  onChange={(value) => updateConfig('fairness_metrics', value)}
                  style={{ width: '100%', marginBottom: '8px' }}
                  placeholder="Select fairness metrics to calculate"
                >
                  {FAIRNESS_METRICS.map(metric => (
                    <Option key={metric.value} value={metric.value}>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{metric.label}</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>{metric.description}</div>
                      </div>
                    </Option>
                  ))}
                </Select>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  Choose which fairness metrics to calculate for your models.
                </div>
              </div>

              {/* Protected Attributes */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                  Protected Attributes
                  <span style={{ fontWeight: 'normal', color: '#888', marginLeft: '8px' }}>
                    ({configData.protected_attributes?.length || 0} selected)
                  </span>
                </label>
                <Select
                  mode="multiple"
                  value={configData.protected_attributes || []}
                  onChange={(value) => updateConfig('protected_attributes', value)}
                  style={{ width: '100%', marginBottom: '8px' }}
                  placeholder="Select protected attributes to test"
                >
                  {PROTECTED_ATTRIBUTES.map(attr => (
                    <Option key={attr.value} value={attr.value}>
                      {attr.icon} {attr.label}
                    </Option>
                  ))}
                </Select>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  Select demographic attributes to test for bias (e.g., gender, race, age).
                </div>
                {configData.protected_attributes?.length === 0 && (
                  <Alert
                    type="warning"
                    message="No protected attributes selected"
                    description="You must select at least one protected attribute to enable bias testing."
                    showIcon
                    style={{ marginTop: '12px' }}
                  />
                )}
              </div>

              {/* Bias Threshold */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                  Bias Threshold: {configData.bias_threshold || 0.2} (Max Acceptable Bias)
                </label>
                <div style={{ paddingLeft: '8px', paddingRight: '8px' }}>
                  <Slider
                    min={0}
                    max={1}
                    step={0.05}
                    value={configData.bias_threshold || 0.2}
                    onChange={(value) => updateConfig('bias_threshold', value)}
                    marks={{
                      0: '0 (No bias)',
                      0.2: '0.2 (Default)',
                      0.5: '0.5',
                      1: '1 (Max)'
                    }}
                  />
                </div>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '12px' }}>
                  <strong>0.0</strong>: No bias allowed (strict) | <strong>0.2</strong>: 20% difference allowed (recommended) | <strong>1.0</strong>: Maximum tolerance
                </div>
              </div>

              {/* Test Frequency */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                  Test Frequency
                </label>
                <Select
                  value={configData.test_frequency || 'quarterly'}
                  onChange={(value) => updateConfig('test_frequency', value)}
                  style={{ width: '100%', marginBottom: '8px' }}
                >
                  <Option value="pre_deployment">Pre-Deployment Only</Option>
                  <Option value="quarterly">Quarterly</Option>
                  <Option value="monthly">Monthly</Option>
                  <Option value="continuous">Continuous</Option>
                </Select>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  How often should bias tests be run? Pre-deployment testing is always recommended.
                </div>
              </div>

              {/* Summary */}
              <Alert
                type="success"
                message="Configuration Summary"
                description={
                  <div>
                    <p><strong>Metrics:</strong> {configData.fairness_metrics?.join(', ') || 'None selected'}</p>
                    <p><strong>Protected Attributes:</strong> {configData.protected_attributes?.join(', ') || 'None selected'}</p>
                    <p><strong>Bias Threshold:</strong> {configData.bias_threshold || 0.2}</p>
                    <p><strong>Test Frequency:</strong> {configData.test_frequency || 'quarterly'}</p>
                  </div>
                }
                showIcon
                style={{ marginTop: '16px' }}
              />
            </div>
          </Panel>
        </Collapse>
      </Card>
    </>
  );
}

export default CTRL_07_ConfigPanel;
